import { useState } from 'react';
import { Gavel, History, Search } from 'lucide-react';
import { Card, CardHeader } from '@/components/ui/Card';
import { Alert } from '@/components/ui/Alert';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { Tabs } from '@/components/ui/Tabs';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { FilterChips } from '@/components/ui/FilterChips';
import { Table, TableHead, TableHeaderCell, TableBody } from '@/components/ui/Table';

/** Real action types from cogs/moderation.py — kick, ban, tempban, timeout, warn, clear, softban, nickname. */
const actionTypes = ['Kick', 'Ban', 'Timeout', 'Warn', 'Clear', 'Softban', 'Nickname'];

export function ModerationPage() {
  const [tab, setTab] = useState('actions');
  const [modalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [historyFilter, setHistoryFilter] = useState<string | null>(null);

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-h1 text-text-primary mb-1">Moderation</h1>
          <p className="text-small text-text-secondary">Kick, ban, timeout, warn, and case history.</p>
        </div>
      </div>

      <Alert
        tone="info"
        title="Not connected to your server yet"
        description="Issuing real moderation actions from the web needs the Bot Bridge (Phase 5) plus a bot-side refactor so existing commands can run outside a Discord interaction — see docs/ARCHITECTURE.md §3.3. Case history is read-only and needs no refactor, so it's planned to connect first (docs/ROADMAP.md MVP)."
      />

      <Tabs
        tabs={[
          { id: 'actions', label: 'Actions' },
          { id: 'history', label: 'History' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'actions' ? (
        <Card>
          <CardHeader
            title="Find a member"
            action={
              <Button variant="primary" onClick={() => setModalOpen(true)} disabled={!searchQuery}>
                <Gavel size={16} aria-hidden="true" />
                New action
              </Button>
            }
          />
          <div className="flex gap-2">
            <Input
              placeholder="Search by username or ID"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search members"
            />
            <Button variant="secondary" disabled>
              <Search size={16} aria-hidden="true" />
            </Button>
          </div>
          {!searchQuery ? (
            <div className="mt-2">
              <EmptyState
                icon={Gavel}
                title="No member selected"
                description="Search for a member to kick, ban, timeout, or warn once your server is connected."
              />
            </div>
          ) : null}
        </Card>
      ) : (
        <Card>
          <CardHeader title="Case history" />
          <div className="mb-4">
            <FilterChips options={actionTypes} selected={historyFilter} onSelect={setHistoryFilter} />
          </div>
          <Table>
            <TableHead>
              <TableHeaderCell>Member</TableHeaderCell>
              <TableHeaderCell>Action</TableHeaderCell>
              <TableHeaderCell>Reason</TableHeaderCell>
              <TableHeaderCell>Moderator</TableHeaderCell>
              <TableHeaderCell>Date</TableHeaderCell>
            </TableHead>
            <TableBody />
          </Table>
          <div className="mt-2">
            <EmptyState
              icon={History}
              title="No cases to show yet"
              description="Case history appears here once your server is connected — nothing is fabricated in the meantime."
            />
          </div>
        </Card>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`Issue action against ${searchQuery || 'member'}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" disabled>
              Confirm
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="text-small text-text-secondary block mb-1">Action</label>
            <Select defaultValue="Warn">
              {actionTypes.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="text-small text-text-secondary block mb-1">Reason</label>
            <Input placeholder="Reason (required, matching Discord's own convention)" />
          </div>
          <p className="text-small text-text-tertiary">
            This is a preview of the flow — confirming does nothing until the Bot Bridge exists.
          </p>
        </div>
      </Modal>
    </div>
  );
}
